import { previewRgb, type Color, type Layer } from '@besbello/design-core';

/**
 * «ESTO NO SE VA A VER»: capas casi del color de la prenda.
 *
 * Un texto marino sobre una camiseta marina se acepta, se paga, y llega una
 * prenda donde el estampado no se distingue. Nadie en la cadena lo paraba: la
 * validación isomorfa no conoce el color de la prenda A PROPÓSITO —meterlo
 * obligaría a tocar el contrato compartido con el servidor y el oráculo de
 * paridad para un aviso que no afecta a producción—, así que este aviso vive
 * solo en el editor y es ORIENTATIVO: ni bloquea guardar ni bloquea comprar.
 *
 * El listón es un ratio de contraste (WCAG) < 1.6: no busca legibilidad de
 * párrafo, busca «se confunde con la tela». Blanco sobre blanco da 1.0;
 * marino sobre marino, ~1.1; negro sobre blanco, 21.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface AvisoContraste {
  layerId: string;
  /** El parche de un toque que lo arregla, por el mismo camino que onFix. */
  arreglo: Partial<Layer>;
}

const UMBRAL_RATIO = 1.6;

function luminancia({ r, g, b }: Rgb): number {
  const f = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function ratioDeContraste(a: Rgb, b: Rgb): number {
  const la = luminancia(a);
  const lb = luminancia(b);
  const [alto, bajo] = la > lb ? [la, lb] : [lb, la];
  return (alto + 0.05) / (bajo + 0.05);
}

/** Blanco o negro, lo que más contraste haga con la prenda. */
export function tintaVisibleSobre(prenda: Rgb): Color {
  return luminancia(prenda) > 0.4
    ? { type: 'rgb', r: 17, g: 17, b: 17 }
    : { type: 'rgb', r: 255, g: 255, b: 255 };
}

const seConfunde = (color: Color, prenda: Rgb): boolean =>
  ratioDeContraste(previewRgb(color), prenda) < UMBRAL_RATIO;

/**
 * Los avisos de un documento contra la prenda elegida.
 *
 * Una capa solo avisa si TODO lo suyo se confunde: un texto marino con
 * contorno blanco se ve perfectamente, y avisar ahí sería ruido que enseña a
 * ignorar los avisos. Las imágenes se saltan: no se puede saber qué colores
 * traen sin analizarlas, y un falso aviso es peor que ninguno.
 */
export function avisosDeContraste(layers: readonly Layer[], prenda: Rgb): AvisoContraste[] {
  const avisos: AvisoContraste[] = [];

  for (const capa of layers) {
    if (capa.kind === 'text') {
      const colorInvisible = seConfunde(capa.color, prenda);
      const contornoVisible = capa.outline !== undefined && !seConfunde(capa.outline.color, prenda);
      if (colorInvisible && !contornoVisible) {
        avisos.push({ layerId: capa.id, arreglo: { color: tintaVisibleSobre(prenda) } as Partial<Layer> });
      }
    } else if (capa.kind === 'shape') {
      const rellenoInvisible = capa.fill === undefined || seConfunde(capa.fill, prenda);
      const trazoVisible =
        capa.stroke !== undefined && (capa.strokeWidthMm ?? 0) > 0 && !seConfunde(capa.stroke, prenda);
      // Una forma sin relleno Y sin trazo visible no es un problema de
      // contraste, es una forma vacía: eso ya lo dice otra validación.
      if (capa.fill !== undefined && rellenoInvisible && !trazoVisible) {
        avisos.push({ layerId: capa.id, arreglo: { fill: tintaVisibleSobre(prenda) } as Partial<Layer> });
      }
    }
  }

  return avisos;
}
